import { createContext, use, useState, useEffect } from 'react';
import { StorageContext } from '@/lib/context';
import { fetchFootnotesFromStorage } from './footnote-data-service';
import { parseRef } from '@/lib/utils/reference-utils';

/**
 * Feature flag: set to `false` to disable footnotes and cross-references.
 * When disabled, the provider never loads data — all queries return empty results.
 */
const ENABLE_FOOTNOTES = true;

interface FootnoteDataContextType {
    footnoteMap: Map<string, TranslationFootnote[]> | null;
    crossRefMap: Map<string, TranslationFootnote[]> | null;
    isLoading: boolean;
    getFootnotesForVerse: (ref: string) => TranslationFootnote[];
    getCrossReferencesForPassage: (book: string, passageRef: string) => TranslationFootnote[];
}

export const FootnoteDataContext = createContext<FootnoteDataContextType | undefined>(undefined);

export function FootnoteDataProvider({ children }: { children: React.ReactNode }) {
    const storage = use(StorageContext);

    // Combine state to prevent double re-renders
    const [state, setState] = useState<{
        footnoteMap: Map<string, TranslationFootnote[]> | null;
        crossRefMap: Map<string, TranslationFootnote[]> | null;
        isLoading: boolean;
    }>({
        footnoteMap: null,
        crossRefMap: null,
        isLoading: ENABLE_FOOTNOTES
    });

    useEffect(() => {
        if (!ENABLE_FOOTNOTES) return;

        // Lazy load footnotes after mount (not during hydration)
        fetchFootnotesFromStorage('csb', storage)
            .then(data => {
                setState({
                    footnoteMap: data.footnoteMap,
                    crossRefMap: data.crossRefMap,
                    isLoading: false
                });
            })
            .catch(err => {
                console.error('Failed to load footnotes:', err);
                setState({ footnoteMap: null, crossRefMap: null, isLoading: false });
            });
    }, [storage]);

    const getFootnotesForVerse = (ref: string): TranslationFootnote[] => {
        if (!state.footnoteMap) return [];
        return state.footnoteMap.get(ref) || [];
    };

    const getCrossReferencesForPassage = (book: string, passageRef: string): TranslationFootnote[] => {
        if (!state.crossRefMap) return [];

        const range = parseRef(passageRef);
        if (!range) return [];

        const results: TranslationFootnote[] = [];

        // Targeted lookup: only check verses within the passage range
        for (let ch = range.chapterStart; ch <= range.chapterEnd; ch++) {
            const vStart = (ch === range.chapterStart) ? range.verseStart : 1;
            const vEnd = (ch === range.chapterEnd) ? range.verseEnd : 999;

            for (let v = vStart; v <= vEnd; v++) {
                const verseRef = `csb:${book}:${ch}:${v}`;
                const crossRefs = state.crossRefMap.get(verseRef) || [];
                results.push(...crossRefs);
            }
        }

        return results;
    };

    return (
        <FootnoteDataContext.Provider value={{
            footnoteMap: state.footnoteMap,
            crossRefMap: state.crossRefMap,
            isLoading: state.isLoading,
            getFootnotesForVerse,
            getCrossReferencesForPassage
        }}>
            {children}
        </FootnoteDataContext.Provider>
    );
}
